'use client';

import { BookingCtaSmallStoryblok } from '@/storyblok/types';
import { StoryblokReactComponent } from './componentsMap';
import Image from 'next/image';
import { DAVINCI_CDN } from '@/app/_constants/defaultValues';
import { MARK_BOLD, render } from 'storyblok-rich-text-react-renderer';
import { storyblokEditable } from '@storyblok/js';
import clsx from 'clsx';
import { makeRichTextRenderOptions } from '@/storyblok/utils/richText';
import { fireGTMEvent } from '@/app/(marketing)/gtm';
import { getImageDimensions } from '@/storyblok/utils/asset';
import { monaFont } from '@/styles/fonts';

export const StoryblokBookingCtaSmall: StoryblokReactComponent<BookingCtaSmallStoryblok> = ({ component, story }) => {
  const mobileImage = component.mobileImage?.filename
    ? { src: component.mobileImage.filename, ...getImageDimensions(component.mobileImage) }
    : { src: `${DAVINCI_CDN}/inclinic/images/booking-cta-small-default-mobile-image.png`, width: 960, height: 820 };
  const desktopImage = component.desktopImage?.filename
    ? { src: component.desktopImage.filename, ...getImageDimensions(component.desktopImage) }
    : { src: `${DAVINCI_CDN}/inclinic/images/booking-cta-small-default-image.png`, width: 1080, height: 616 };

  const background = !!component.background ? component.background : 'white';
  return (
    <div
      className={clsx(
        'relative z-[2] mx-4 flex flex-col items-stretch gap-6 rounded-3xl border p-6 shadow-1 lg:items-start lg:pr-[14rem]',
        background === 'white' ? 'bg-white' : 'bg-brandGreen-400',
      )}
      {...storyblokEditable(component)}
    >
      {component.mobileImageBeforeContent && (
        <>
          <div className="-mb-12 aspect-square w-full lg:hidden"></div>
          <Image className="absolute  right-0 top-0 w-full lg:hidden" src={mobileImage} alt="Medico" aria-hidden />
        </>
      )}
      <div className="flex flex-col gap-3">
        <h2
          className={clsx(
            monaFont.className,
            'text-2xl font-bold leading-tight',
            background === 'white' ? 'text-neutral-darker' : 'text-white',
          )}
        >
          {render(
            component.title,
            makeRichTextRenderOptions(story, {
              markResolvers: {
                [MARK_BOLD]: children => <strong className="font-medium">{children}</strong>,
              },
            }),
          )}
        </h2>
        <p className={clsx('text-lg', background === 'white' ? 'text-neutral-darker' : 'text-white')}>
          Cerca la prestazione medica di cui hai bisogno.
        </p>
      </div>
      <form
        className="relative z-10 flex flex-col gap-3 lg:flex-row lg:p-1"
        action="/cerca"
        onSubmit={e => {
          const formData = new FormData(e.target as HTMLFormElement);
          fireGTMEvent('blog_click_città', { city: formData.get('location'), speciality: formData.get('service') });
        }}
      >
        <div className="relative flex flex-grow flex-row items-center rounded-lg bg-brandGreen-50">
          <Image
            className="absolute ml-5 flex-shrink-0"
            src={`${DAVINCI_CDN}/inclinic/svgs/medical-services-icon.svg`}
            alt=""
            aria-hidden="true"
            width={24}
            height={25}
          />
          <input
            defaultValue={component.speciality || component.manualSpeciality}
            name="service"
            required
            className={clsx(
              'w-full min-w-48 flex-grow rounded-lg py-3 pl-14 pr-5 focus:outline-1 focus:outline-[#0D9488]',
              background === 'white' ? 'bg-brandGreen-50' : 'bg-neutral-surface',
            )}
            placeholder="Es. Psicologo"
          />
        </div>
        <div className="relative flex flex-grow flex-row items-center rounded-lg bg-brandGreen-50">
          <Image
            className="absolute ml-5 flex-shrink-0"
            src={`${DAVINCI_CDN}/inclinic/svgs/location-marker-icon.svg`}
            alt=""
            aria-hidden="true"
            width={20}
            height={20}
          />
          <input
            name="location"
            required
            className={clsx(
              'w-full min-w-48 flex-grow rounded-lg py-3 pl-14 pr-5 focus:outline-1 focus:outline-[#0D9488]',
              background === 'white' ? 'bg-brandGreen-50' : 'bg-neutral-surface',
            )}
            placeholder="Es. Milano"
          />
        </div>
        <button
          className={clsx(
            'rounded-lg py-3 text-lg font-semibold text-white shadow-lg transition-colors lg:px-4 lg:py-3',
            background === 'white'
              ? 'bg-secondary hover:bg-secondary-hover focus:bg-secondary-hover'
              : 'bg-primary hover:bg-primary-hover focus:bg-primary-hover',
          )}
          type="submit"
          aria-label="Cerca"
        >
          <span>Cerca</span>
        </button>
      </form>

      <Image className="absolute bottom-0 right-0 hidden h-full w-auto lg:block" src={desktopImage} alt="Medico" aria-hidden />
      {!component.mobileImageBeforeContent && (
        <>
          <div className="-mt-12 aspect-square w-full lg:hidden"></div>
          <Image className="absolute  bottom-0 right-0 w-full lg:hidden" src={mobileImage} alt="Medico" aria-hidden />
        </>
      )}
    </div>
  );
};
